/**
 * Device enrollment. Right after `whoami` confirms a signed-in visitor, the shell
 * enrolls this browser as a device with the profile API so the backend knows every
 * client a session is active on (see {@link AuthProvider}).
 *
 * The device's identity is a non-extractable ECDSA key held in IndexedDB (see
 * {@link getDeviceKeyIdentity}); enrollment sends its public JWK and thumbprint, and the
 * thumbprint is the device id. No random-UUID/localStorage identity is kept — possession
 * of the private key, not a guessable string, is what identifies the device.
 *
 * Same-origin like `whoami`: the request rides the session cookie with no
 * `credentials`/CORS handling. Being a POST it must carry the session CSRF token as
 * the root-level `__csrfToken` body field — the content API rejects any mutating
 * request without it (see the watch pilet's playtoken call for the same convention).
 */
// The shell's package version, reported as the enrollment's `appVersion`. Imported
// (not hardcoded) so it tracks package.json automatically; webpack 5 tree-shakes the
// JSON module down to just this key, so the rest of package.json never ships.
import { version as APP_VERSION } from '../../package.json'
import { DEVICE_ID_STORAGE_KEY } from '../storage'
import { getDeviceKeyIdentity, dpopFetch } from '@medialib/styleguide'

const DEVICE_ENROLLMENT_URL = '/api/3.0/profile/devices'

/** The heartbeat sub-resource for a single device (singular `device` in the path). */
const deviceHeartbeatUrl = (deviceId: string): string =>
  `/api/3.0/profile/device/${deviceId}/heartbeat`

/** How often the current device pings the heartbeat endpoint to stay "active". */
export const DEVICE_HEARTBEAT_INTERVAL_MS = 30_000

/**
 * The enrollment payload POSTed to the profile device enroll endpoint. The client sends
 * its key identity (public JWK + thumbprint) plus a client-proposed `deviceId`, and the
 * backend answers with the record's persistence identifier.
 *
 * **`deviceId` is mandatory even though nothing reads it back.** The entity column is NOT
 * NULL (`Device::$deviceId`, `@ORM\Column(length=64)`) and `Device::create()` copies the
 * submitted value verbatim — it does NOT apply the `?: $thumbprint` fallback the enroll
 * controller uses for its duplicate lookup. Omitting the field therefore drives a null
 * into that column and the INSERT throws. The controller catches it, sets 500, then
 * unconditionally overwrites the status with 201 and returns
 * `getIdentifierByObject($device)` — which Flow assigns at construction, so an object
 * that was never persisted still yields a fresh UUID. The client sees a plausible 201
 * with an id and enrolls nothing, silently, on every load. Dropping this field is exactly
 * what broke enrollment between the DPoP change and now, so do not "clean it up" again.
 *
 * Its value is the key THUMBPRINT rather than the pre-DPoP `crypto.randomUUID()`: an id
 * derived from the enrolled key cannot be claimed by a client unable to prove possession
 * of it, and it matches the fallback the controller's duplicate lookup already uses, so
 * re-enrolling the same browser stays idempotent.
 */
export interface DeviceEnrollment {
  /**
   * The device id the record is keyed on, stored verbatim as the entity's non-nullable
   * `deviceId` column and surfaced by the device list as `device_id`. Set to
   * {@link thumbprint} — see the module docblock for why this field must be sent.
   */
  deviceId: string;
  /**
   * The device key's public JWK — the material the backend records so it can later
   * verify a proof-of-possession signature.
   */
  pubJwk: JsonWebKey;
  /** The public key's RFC 7638 thumbprint, identifying the key that is enrolling. */
  thumbprint: string;
  platform: string;
  model: string;
  osVersion: string;
  appVersion: string;
  pushToken: string | null;
  name: string;
  locale: string;
  timezone: string;
}

/**
 * The enroll endpoint's response — `{ id }` only, the record's Flow persistence
 * identifier (the same `id` the device list returns, NOT its `device_id`).
 *
 * Either one addresses the device: the API resolves a `/api/3.0/profile/device/{…}/`
 * path segment through a converter that matches the persistence identifier, `deviceId`
 * OR `thumbprint`, so the heartbeat and the profile pilet's remove call are free to use
 * whichever they hold. The rest of the shape is owned by the API, so this stays open.
 */
export interface DeviceEnrollmentResponse {
  /** The record's persistence identifier, accepted by every `/device/{…}/` route. */
  id?: string;
  [key: string]: unknown;
}

/**
 * The backend-generated device id for this browser, cached in memory for the session so
 * the heartbeat has it even when localStorage is blocked. Set from the enrollment
 * response; lazily seeded from the localStorage mirror (a prior session's id) on first
 * read.
 */
let currentDeviceId: string | null = null

/**
 * This browser's device id — the backend-generated id enrollment returned. Also written
 * to {@link DEVICE_ID_STORAGE_KEY} as a PUBLIC mirror (not secret material — the private
 * key lives non-extractably in IndexedDB, never here) so the profile pilet's device list
 * can mark this browser. Null before enrollment has ever succeeded on this browser.
 */
export function getDeviceId (): string | null {
  if (currentDeviceId !== null) return currentDeviceId
  try {
    currentDeviceId = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY)
  } catch {
    currentDeviceId = null
  }
  return currentDeviceId
}

/** Store the device id in memory and mirror it to localStorage for cross-pilet reads. */
function setDeviceId (id: string): void {
  currentDeviceId = id
  try {
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, id)
  } catch {
    // Can't persist across reloads (private mode / blocked); the in-memory copy still
    // serves this session's heartbeat.
  }
}

/**
 * Build the enrollment for this browser: the device key's public JWK and thumbprint plus
 * the client descriptors. A web client has no native platform/model/OS, so it reports
 * the web platform and the closest browser-provided descriptors; there is no web-push
 * subscription, so `pushToken` is null. Rejects if the device key can't be produced (no
 * WebCrypto/IndexedDB) — there is no key identity to enroll without it.
 */
async function buildEnrollment (): Promise<DeviceEnrollment> {
  const { pubJwk, thumbprint } = await getDeviceKeyIdentity()

  const nav = window.navigator
  let timezone = ''
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    // No Intl time-zone support — leave it empty rather than fail the enrollment.
  }
  return {
    deviceId: thumbprint,
    pubJwk,
    thumbprint,
    platform: 'web',
    model: nav.userAgent,
    osVersion: nav.platform || '',
    appVersion: APP_VERSION,
    pushToken: null,
    name: 'Web',
    locale: nav.language || '',
    timezone
  }
}

/**
 * Enroll this device with the profile API. `csrfToken` comes from the shell's
 * shared domain configuration; it is attached as the root-level `__csrfToken` field
 * (not nested), while the device fields ride under a `device` key. The persistence
 * identifier the backend returns as `id` is stored (see {@link setDeviceId}) and becomes
 * the id every subsequent device request uses. Rejects on any non-2xx (or if the
 * device key is unavailable) so the caller can decide how a failed enrollment affects
 * the UI.
 */
export async function enrollDevice (csrfToken: string): Promise<DeviceEnrollmentResponse> {
  const device = await buildEnrollment()
  // A DPoP chokepoint: the proof header lets the backend confirm the caller holds the
  // very key being enrolled (its header JWK must match the enrolled `pubJwk`).
  const res = await dpopFetch(DEVICE_ENROLLMENT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ __csrfToken: csrfToken, device })
  })
  if (!res.ok) throw new Error(`Device enrollment failed: ${res.status}`)
  const body = await res.json() as DeviceEnrollmentResponse
  // The returned persistence identifier is authoritative — persist it as the id the
  // heartbeat and the profile device list use from here on.
  if (typeof body.id === 'string' && body.id) {
    setDeviceId(body.id)
  } else {
    console.warn('[auth] device enrollment response missing id', body)
  }
  return body
}

/**
 * Ping the heartbeat endpoint for THIS browser's device, keeping its session state
 * "active". Targets the backend-generated id stored at enrollment. A mutating POST, so
 * it carries the session CSRF token at the body root. Rejects when there is no enrolled
 * id yet, or on any non-2xx, so the caller can log a missed beat.
 */
export async function sendDeviceHeartbeat (csrfToken: string): Promise<void> {
  const deviceId = getDeviceId()
  if (deviceId === null) throw new Error('Device heartbeat skipped: device not enrolled')
  const res = await dpopFetch(deviceHeartbeatUrl(deviceId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ __csrfToken: csrfToken })
  })
  if (!res.ok) throw new Error(`Device heartbeat failed: ${res.status}`)
}
